import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { playersAPI, API_URL } from '../services/api';
import './PlayerDashboard.css';

interface PlayerData {
  player: any;
  teams: any[];
  rankings: any[];
  evaluations: any[];
  trialPeriods: any[];
}

const PlayerDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<PlayerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'profile' | 'rankings' | 'evaluations' | 'history' | 'teams'>('profile');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedRankingView, setSelectedRankingView] = useState<string>(''); // '' will be set to first team or 'overall'
  const [selectedTrialPeriod, setSelectedTrialPeriod] = useState<string>('all'); // 'all' for all periods
  
  // Redirect coaches to admin view
  useEffect(() => {
    if (user?.player?.position === 'Coach') {
      navigate('/');
    }
  }, [user, navigate]);
  
  // Check if player is recreational
  const isRecreational = user?.player?.playerType === 'Recreational';

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      // Pass trial period ID if not 'all'
      const periodParam = selectedTrialPeriod !== 'all' ? selectedTrialPeriod : undefined;
      const response = await playersAPI.getDashboard(user?.player?.id, periodParam);
      setDashboardData(response.data);
      setError('');
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [user, selectedTrialPeriod]);

  useEffect(() => {
    if (!user?.player?.id) {
      setError('Player information not found');
      setLoading(false);
      return;
    }

    loadDashboardData();
  }, [user, loadDashboardData]);

  // Set default ranking view when dashboard data loads
  useEffect(() => {
    if (dashboardData && dashboardData.teams && dashboardData.teams.length > 0) {
      // Default to first team - only set if empty string
      if (selectedRankingView === '') {
        setSelectedRankingView(dashboardData.teams[0].id);
      }
    } else if (dashboardData && (!dashboardData.teams || dashboardData.teams.length === 0)) {
      // No teams, default to overall - only set if empty string
      if (selectedRankingView === '') {
        setSelectedRankingView('overall');
      }
    }
  }, [dashboardData, selectedRankingView]);

  const handleEditProfile = () => {
    navigate('/player/profile');
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingPhoto(true);
      await playersAPI.uploadPhoto(user?.player?.id, file);
      // Reload dashboard to show new photo
      await loadDashboardData();
    } catch (err: any) {
      console.error('Failed to upload photo:', err);
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="player-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="player-dashboard">
        <div className="error-message">{error || 'Failed to load dashboard'}</div>
      </div>
    );
  }

  const { player, teams, rankings, evaluations, trialPeriods } = dashboardData;

  // Filter evaluations by trial period
  const filteredEvaluations = selectedTrialPeriod === 'all' 
    ? evaluations 
    : evaluations.filter((e: any) => e.trialPeriodId === selectedTrialPeriod);

  return (
    <div className="player-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="player-info-header">
            <div className="player-details-header">
              <h1>{player.firstName} {player.lastName}</h1>
              {player.position && <p className="position">Position: {player.position}</p>}
              {player.jerseyNumber && <p className="jersey">Jersey #: {player.jerseyNumber}</p>}
            </div>
          </div>
          <div className="header-actions">
            <button onClick={handleEditProfile} className="btn-edit">Edit Profile</button>
            <button onClick={handleLogout} className="btn-logout">Logout</button>
          </div>
        </div>
      </div>

      <div className="dashboard-tabs">
        <button 
          className={activeTab === 'profile' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('profile')}
        >
          Profile
        </button>
        {!isRecreational && (
          <button 
            className={activeTab === 'rankings' ? 'tab active' : 'tab'}
            onClick={() => setActiveTab('rankings')}
          >
            My Rankings
          </button>
        )}
        <button 
          className={activeTab === 'evaluations' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('evaluations')}
        >
          My Evaluations
        </button>
        <button 
          className={activeTab === 'history' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('history')}
        >
          Evaluation History
        </button>
        <button 
          className={activeTab === 'teams' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('teams')}
        >
          My Teams
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'profile' && player && (
          <div className="profile-tab">
            {/* Photo Section */}
            <div className="info-section photo-section">
              <h2>Profile Photo</h2>
              <div className="photo-upload-container">
                <div className="photo-display">
                  {player.photoUrl ? (
                    <img 
                      src={`${API_URL}${player.photoUrl}`} 
                      alt={`${player.firstName} ${player.lastName}`}
                      className="profile-photo-display"
                    />
                  ) : (
                    <div className="photo-placeholder-large">No Photo</div>
                  )}
                </div>
                <div className="photo-upload-action">
                  <label htmlFor="photo-upload" className="btn-upload-photo">
                    {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <p className="upload-hint">Click to upload a new profile photo</p>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h2>Personal Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>First Name:</label>
                  <span>{player.firstName}</span>
                </div>
                <div className="info-item">
                  <label>Last Name:</label>
                  <span>{player.lastName}</span>
                </div>
                <div className="info-item">
                  <label>Date of Birth:</label>
                  <span>{player.dateOfBirth ? new Date(player.dateOfBirth).toLocaleDateString() : 'Not set'}</span>
                </div>
                <div className="info-item">
                  <label>Position:</label>
                  <span>{player.position || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <label>Jersey Number:</label>
                  <span>{player.jerseyNumber || 'Not set'}</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h2>Guardian 1 Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name:</label>
                  <span>{player.guardian1Name || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{player.guardian1Email || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <label>Phone:</label>
                  <span>{player.guardian1Phone || 'Not set'}</span>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h2>Guardian 2 Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>Name:</label>
                  <span>{player.guardian2Name || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <label>Email:</label>
                  <span>{player.guardian2Email || 'Not set'}</span>
                </div>
                <div className="info-item">
                  <label>Phone:</label>
                  <span>{player.guardian2Phone || 'Not set'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rankings' && (
          <div className="rankings-tab">
            <div className="tab-header-with-toggle">
              <div>
                <h2>My Rankings</h2>
                <p className="info-text">
                  Your position compared to {selectedRankingView === 'overall' ? 'all competition players' : 'players on your team'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                {rankings.length > 0 && rankings[0].teamRankings && rankings[0].teamRankings.length > 0 && (
                  <div className="ranking-selector">
                    <label htmlFor="ranking-view">View: </label>
                    <select 
                      id="ranking-view"
                      value={selectedRankingView} 
                      onChange={(e) => setSelectedRankingView(e.target.value)}
                      className="ranking-dropdown"
                    >
                      {rankings[0].teamRankings.map((teamRank: any) => (
                        <option key={teamRank.teamId} value={teamRank.teamId}>
                          {teamRank.teamName}
                        </option>
                      ))}
                      <option value="overall">Overall</option>
                    </select>
                  </div>
                )}
                {trialPeriods && trialPeriods.length > 0 && (
                  <div className="ranking-selector">
                    <label htmlFor="ranking-period">Period: </label>
                    <select 
                      id="ranking-period"
                      value={selectedTrialPeriod} 
                      onChange={(e) => setSelectedTrialPeriod(e.target.value)}
                      className="ranking-dropdown"
                    >
                      <option value="all">All Periods</option>
                      {trialPeriods.map((period: any) => (
                        <option key={period.id} value={period.id}>
                          {period.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            {rankings.length === 0 ? (
              <p className="no-data">No ranking data available yet.</p>
            ) : (
              <div className="rankings-simple">
                {rankings.map((ranking: any, index: number) => {
                  // Find the selected team's rankings or use overall
                  let overallRank, physicalRank, technicalRank, totalPlayers, teamName;
                  
                  if (selectedRankingView === 'overall') {
                    overallRank = ranking.overallRank;
                    physicalRank = ranking.physicalRank;
                    technicalRank = ranking.technicalRank;
                    totalPlayers = ranking.totalPlayers;
                    teamName = null;
                  } else {
                    // Find the selected team in teamRankings
                    const selectedTeam = ranking.teamRankings?.find((tr: any) => tr.teamId === selectedRankingView);
                    if (selectedTeam) {
                      overallRank = selectedTeam.overallRank;
                      physicalRank = selectedTeam.physicalRank;
                      technicalRank = selectedTeam.technicalRank;
                      totalPlayers = selectedTeam.totalPlayers;
                      teamName = selectedTeam.teamName;
                    } else {
                      // Fallback to overall
                      overallRank = ranking.overallRank;
                      physicalRank = ranking.physicalRank;
                      technicalRank = ranking.technicalRank;
                      totalPlayers = ranking.totalPlayers;
                      teamName = null;
                    }
                  }

                  return (
                    <div key={index} className="rankings-content">
                      {teamName && (
                        <div className="team-context">
                          <strong>Team: {teamName}</strong>
                        </div>
                      )}
                      <div className="rank-line overall">
                        <span className="rank-label">Overall Ranking:</span>
                        <span className="rank-value">
                          #{overallRank || 'N/A'} of {totalPlayers || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="rank-line physical">
                        <span className="rank-label">Physical Ranking:</span>
                        <span className="rank-value">
                          #{physicalRank || 'N/A'} of {totalPlayers || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="rank-line technical">
                        <span className="rank-label">Technical Ranking:</span>
                        <span className="rank-value">
                          #{technicalRank || 'N/A'} of {totalPlayers || 'N/A'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'evaluations' && (
          <div className="evaluations-tab">
            <div className="tab-header-with-toggle">
              <div>
                <h2>My Evaluations</h2>
                <p className="info-text">
                  Your individual evaluation results {!isRecreational && `(ranks compared to ${selectedRankingView === 'overall' ? 'all competition players' : 'your team'})`}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                {!isRecreational && dashboardData?.teams && dashboardData.teams.length > 0 && (
                  <div className="ranking-selector">
                    <label htmlFor="eval-ranking-view">View: </label>
                    <select 
                      id="eval-ranking-view"
                      value={selectedRankingView} 
                      onChange={(e) => setSelectedRankingView(e.target.value)}
                      className="ranking-dropdown"
                    >
                      {dashboardData.teams.map((team: any) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                      <option value="overall">Overall</option>
                    </select>
                  </div>
                )}
                {trialPeriods && trialPeriods.length > 0 && (
                  <div className="ranking-selector">
                    <label htmlFor="eval-period">Period: </label>
                    <select 
                      id="eval-period"
                      value={selectedTrialPeriod} 
                      onChange={(e) => setSelectedTrialPeriod(e.target.value)}
                      className="ranking-dropdown"
                    >
                      <option value="all">All Periods</option>
                      {trialPeriods.map((period: any) => (
                        <option key={period.id} value={period.id}>
                          {period.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            {filteredEvaluations.length === 0 ? (
              <p className="no-data">No evaluation data available for the selected period.</p>
            ) : (
              <div className="evaluations-list">
                {/* Sort evaluations: 1st Touch first, then other numbers, then alphabetical */}
                {[...filteredEvaluations].sort((a, b) => {
                  const aName = a.categoryName || '';
                  const bName = b.categoryName || '';
                  
                  // 1st Touch Ball Launcher always first
                  if (aName.includes('1st Touch')) return -1;
                  if (bName.includes('1st Touch')) return 1;
                  
                  // Then other number-prefixed categories
                  const aStartsWithNumber = /^\d/.test(aName);
                  const bStartsWithNumber = /^\d/.test(bName);
                  if (aStartsWithNumber && !bStartsWithNumber) return -1;
                  if (!aStartsWithNumber && bStartsWithNumber) return 1;
                  
                  // Finally alphabetical
                  return aName.localeCompare(bName);
                }).map((evaluation: any, index: number) => {
                  // Debug: log the evaluation data
                  console.log('Evaluation:', evaluation.categoryName, 'Data:', evaluation.data);
                  
                  return (
                  <div key={index} className="evaluation-card">
                    <h3>{evaluation.categoryName}</h3>
                    <p className="evaluation-description">{evaluation.categoryDescription}</p>
                    
                    <div className="evaluation-metrics">
                      {/* Dynamically render all metrics from the data object */}
                      {evaluation.data && Object.keys(evaluation.data).length > 0 && (
                        <>
                          {Object.keys(evaluation.data).map((key) => {
                            // Skip if it's any rank field (we'll show the appropriate one with its value)
                            if (key.includes('Rank')) return null;
                            
                            // Determine which rank to show based on dropdown selection
                            let rankValue;
                            if (selectedRankingView === 'overall') {
                              rankValue = evaluation.data[key + ' Rank'];
                            } else {
                              // Get team-specific rank from teamMetricRanks
                              rankValue = evaluation.teamMetricRanks?.[selectedRankingView]?.[key] || evaluation.data[key + ' Rank'];
                            }
                            
                            return (
                              <div key={key} className="metric-row">
                                <div className="metric-item">
                                  <label>{key}:</label>
                                  <span className="value">{evaluation.data[key]}</span>
                                </div>
                                {rankValue && !isRecreational && (
                                  <div className="metric-item">
                                    <label>Rank:</label>
                                    <span className="rank">#{rankValue}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      )}
                    </div>
                    
                    <div className="evaluation-date">
                      Evaluated: {new Date(evaluation.createdAt).toLocaleDateString()}
                      {evaluation.trialPeriod && ` • ${evaluation.trialPeriod.name}`}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="history-tab">
            <h2>Evaluation History</h2>
            <p className="info-text">
              Track your progress over time
            </p>
            {evaluations.length === 0 ? (
              <p className="no-data">No evaluation history available yet.</p>
            ) : (
              <div className="history-message">
                <p>View your complete evaluation history by clicking the button below:</p>
                <button 
                  className="btn-view-history"
                  onClick={() => navigate('/evaluation-history')}
                >
                  View Full History
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'teams' && (
          <div className="teams-tab">
            <h2>My Teams</h2>
            {teams.length === 0 ? (
              <p className="no-data">Not assigned to any teams yet.</p>
            ) : (
              <div className="teams-list">
                {teams.map((team: any) => (
                  <div key={team.id} className="team-card">
                    <h3>{team.name}</h3>
                    {team.description && <p className="team-description">{team.description}</p>}
                    <div className="team-info">
                      {team.ageGroup && <span className="badge">Age Group: {team.ageGroup}</span>}
                      {team.season && <span className="badge">Season: {team.season}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerDashboard;
